import React from 'react';
import { resolveRecordButton } from '../lib/callRecording';

/**
 * CallControls
 *
 * Renders the in-call button row. Visibility rules:
 *   • The entire component is only mounted when activeCall is truthy
 *     (parent gates with {activeCall && !showDialpad && <CallControls />}).
 *   • Mute / Keypad / Speaker / End are always interactive — the user
 *     needs to hang up or mute even before the remote leg answers.
 *   • Hold / 3-Way / Transfer are always present in the DOM (so the
 *     layout does not jump when the call connects) but are disabled
 *     and visually dimmed until callConnected becomes true (Twilio SDK
 *     'accept' event has fired and audio is flowing).
 *
 * Hit targets: each ctrl-btn fills the grid cell (icon circle + label).
 * Labels used to overflow a 64x64 circle so taps on the word "Keypad"
 * missed the button entirely (web + iOS + Android).
 */
function KeypadIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="6" cy="6" r="2" />
      <circle cx="12" cy="6" r="2" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="6" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="18" cy="12" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="12" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
    </svg>
  );
}

/** Inline SVG icon set for in-call controls. Each takes an optional
 *  filled prop mirroring the controls' .active state — when active, the
 *  glyph renders solid/filled so the icon shape itself confirms the toggle,
 *  matching native iOS/Android buttons (mute / speaker / hold). All inherit
 *  currentColor so the existing .ctrl-btn.active .ctrl-icon-wrap background
 *  + color rules still apply unchanged. */
function MicIcon({ filled = false }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3z" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
      {filled && <path d="M4 4l16 16" />}
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <ellipse cx="12" cy="12" rx="9" ry="4" opacity="0" />
      <path d="M12 3c3 2.5 3 15.5 0 18" />
    </svg>
  );
}

function SpeakerIcon({ filled = false }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 9v6h3l4 3V6L7 9H4z" fill={filled ? 'currentColor' : 'none'} />
      <path d="M16.5 9a4 4 0 0 1 0 6" />
      <path d="M19 7a7.5 7.5 0 0 1 0 10" />
    </svg>
  );
}

function HoldIcon({ filled = false }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* pause (hold) / play (resume) */}
      <path d={filled ? 'M8 5l11 7-11 7z' : 'M8 5v14M16 5v14'} fill={filled ? 'currentColor' : 'none'} />
    </svg>
  );
}

function AddIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="8" r="3.5" />
      <path d="M4 19a6 6 0 0 1 12 0" />
      <path d="M18 8v6M21 11h-6" />
    </svg>
  );
}

function TransferIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h11a3 3 0 0 1 0 6H9" />
      <path d="M13 9l2-2-2-2" />
      <path d="M20 17H9a3 3 0 0 1 0-6h6" />
      <path d="M11 15l-2 2 2 2" />
    </svg>
  );
}

function HangupIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 9c-2.2 0-4.3.4-6.3 1.1-.6.2-1 .8-1 1.4v2.6c0 .6-.4 1-1 1H2.4c-.6 0-1-.4-1-1v-2.6c0-1.3.7-2.5 1.9-3.1C5.4 8.5 8.6 7.5 12 7.5s6.6 1 8.7 2.4c1.2.6 1.9 1.8 1.9 3.1v2.6c0 .6-.4 1-1 1H20c-.6 0-1-.4-1-1v-2.6c0-.6-.4-1.2-1-1.4C16.3 9.4 14.2 9 12 9z" transform="rotate(180 12 12)" />
    </svg>
  );
}

function RecordIcon({ filled = false }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="9" r="4.2" />
      <circle cx="12" cy="17" r="2.6" />
    </svg>
  );
}

export default function CallControls({
  activeCall,
  callConnected,
  callStatus,         // 'closed' | 'connecting' | 'ringing' | 'open' — informational
  onHangup,
  onMuteToggle,
  isMuted,
  onKeypad,
  onSpeakerToggle,
  isSpeaker,
  isBluetooth,
  onDeviceCycle,
  onHold,
  isOnHold,
  isHoldPending,
  onAdd,
  onTransfer,
  isBridging,
  isTranslating,
  onTranslate,
  recordingSupported,
  isRecording,
  recordingBusy,
  recordingSeconds,
  recordingError,
  onRecord,
  onStop,
}) {
  // Translate unlocks once the call is active (ringing or connected).
  const translateLocked = !(
    callStatus === 'ringing' ||
    callStatus === 'open' ||
    callConnected
  );
  const controlsLocked = !callConnected;
  const rec = resolveRecordButton({
    supported: recordingSupported,
    connected: callConnected,
    translating: isTranslating,
    busy: !!recordingBusy,
    isRecording: !!isRecording,
    seconds: recordingSeconds,
    error: recordingError || null,
  });
  const recordLocked = rec.locked;
  const recBusy = rec.busy;
  const recordTitle = rec.title;
  const lockedTitle = 'Available once the call connects';

  if (!activeCall) return null;

  const handleKeypad = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onKeypad === 'function') onKeypad();
  };

  return (
    <div className="call-controls">
      <div className="call-controls-grid">

        <button
          type="button"
          className={`ctrl-btn ${isMuted ? 'active' : ''}`}
          onClick={onMuteToggle}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <span className="ctrl-icon-wrap"><MicIcon filled={isMuted} /></span>
          <span className="ctrl-label">{isMuted ? 'Unmute' : 'Mute'}</span>
        </button>

        {/* Row 1, not buried on its own row at the bottom — the whole
            point is finding it fast when the other party doesn't speak
            your language, not scanning past every other control first. */}
        <button
          type="button"
          className={`ctrl-btn ${isTranslating ? 'active' : ''} ${translateLocked ? 'locked' : ''}`}
          onClick={translateLocked ? undefined : onTranslate}
          disabled={translateLocked}
          title={translateLocked ? 'Available once dialing starts' : isTranslating ? 'Translation active' : 'Enable live translation'}
          aria-disabled={translateLocked}
        >
          <span className="ctrl-icon-wrap"><GlobeIcon /></span>
          <span className="ctrl-label">{isTranslating ? 'Translating' : 'Translate'}</span>
        </button>

        <button
          type="button"
          className="ctrl-btn"
          onClick={handleKeypad}
          title="Show dialpad"
          data-testid="truva-in-call-keypad"
          aria-label="Show dialpad"
        >
          <span className="ctrl-icon-wrap"><KeypadIcon /></span>
          <span className="ctrl-label">Keypad</span>
        </button>

        <button
          type="button"
          className={`ctrl-btn ${isSpeaker ? 'active' : ''}`}
          onClick={onSpeakerToggle}
          title={isSpeaker ? 'Switch to earpiece' : 'Switch to speaker'}
          data-testid="truva-speaker-button"
        >
          <span className="ctrl-icon-wrap"><SpeakerIcon filled={isSpeaker} /></span>
          <span className="ctrl-label">{isSpeaker ? 'Speaker' : 'Earpiece'}</span>
        </button>

        <button
          type="button"
          className={`ctrl-btn ${isOnHold ? 'active' : ''} ${controlsLocked ? 'locked' : ''}`}
          onClick={controlsLocked ? undefined : onHold}
          disabled={isHoldPending || controlsLocked}
          title={
            controlsLocked
              ? lockedTitle
              : isHoldPending
              ? 'Hold pending…'
              : isOnHold
              ? 'Resume call'
              : 'Hold call'
          }
          aria-disabled={isHoldPending || controlsLocked}
        >
          <span className="ctrl-icon-wrap"><HoldIcon filled={isOnHold} /></span>
          <span className="ctrl-label">{isOnHold ? 'Resume' : 'Hold'}</span>
        </button>

        <button
          type="button"
          className={`ctrl-btn ${controlsLocked ? 'locked' : ''}`}
          onClick={controlsLocked ? undefined : (onAdd || (() => {}))}
          disabled={controlsLocked}
          title={controlsLocked ? lockedTitle : 'Add a third party (3-way call)'}
          aria-disabled={controlsLocked}
        >
          <span className="ctrl-icon-wrap"><AddIcon /></span>
          <span className="ctrl-label">3-Way</span>
        </button>

        <button
          type="button"
          className={`ctrl-btn ${controlsLocked ? 'locked' : ''}`}
          onClick={controlsLocked ? undefined : (onTransfer ? () => onTransfer() : () => {})}
          disabled={controlsLocked}
          title={controlsLocked ? lockedTitle : 'Transfer call to another number'}
          aria-disabled={controlsLocked}
        >
          <span className="ctrl-icon-wrap"><TransferIcon /></span>
          <span className="ctrl-label">Transfer</span>
        </button>


        {rec.visible && (
          <button
            type="button"
            data-testid="truva-record-button"
            className={`ctrl-btn rec ${rec.redDot ? 'active' : ''} ${(recordLocked || recBusy) ? 'locked' : ''}`}
            onClick={(recordLocked || recBusy) ? undefined : (rec.redDot ? onStop : onRecord)}
            disabled={recordLocked || recBusy}
            title={recordTitle}
            aria-disabled={recordLocked || recBusy}
            aria-label={rec.ariaLabel}
          >
            <span className="ctrl-icon-wrap">{rec.redDot ? <span className="rec-dot" aria-hidden="true" /> : <RecordIcon />}</span>
            <span className="ctrl-label">{rec.label}</span>
          </button>
        )}
      </div>

      <button
        type="button"
        className={`ctrl-btn hangup ${isBridging ? 'locked' : ''}`}
        onClick={onHangup}
        disabled={!!isBridging}
        aria-disabled={!!isBridging}
        title={
          isBridging
            ? 'Bridge in progress - End re-enables once the bridge confirms'
            : 'Hang Up'
        }
        data-testid="truva-hangup-button"
      >
        <span className="ctrl-icon-wrap hangup-icon"><HangupIcon /></span>
        <span className="ctrl-label">{isBridging ? 'Bridging…' : 'End'}</span>
      </button>
    </div>
  );
}
